import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Clock, ChevronLeft, ChevronRight, X, Calculator, Flag } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import MathText from '@/components/MathText';
import SEOHead, { seoConfigs } from '@/components/SEOHead';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DiagnosticTest() {
  const navigate = useNavigate();

  const [sessionId, setSessionId] = useState(null);
  const [section, setSection] = useState('rw'); // 'rw' or 'math'
  const [sectionName, setSectionName] = useState('Reading and Writing');
  const [moduleNumber, setModuleNumber] = useState(1);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [timeRemaining, setTimeRemaining] = useState(1920); // 32 minutes for R&W
  const [loading, setLoading] = useState(true);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [email, setEmail] = useState('');
  
  // Break state (between R&W and Math)
  const [onBreak, setOnBreak] = useState(false);
  const [breakTimeRemaining, setBreakTimeRemaining] = useState(600); // 10 minutes
  const [rwScore, setRwScore] = useState(null);
  
  // Calculator
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorType, setCalculatorType] = useState('scientific');
  
  // Bookmarked questions for review
  const [bookmarkedQuestions, setBookmarkedQuestions] = useState(new Set());

  useEffect(() => {
    // Get session ID and email from localStorage
    const storedSessionId = localStorage.getItem('diagnostic_session_id');
    const storedEmail = localStorage.getItem('diagnostic_email');
    const storedSection = localStorage.getItem('diagnostic_section');
    const storedSectionName = localStorage.getItem('diagnostic_section_name');
    const storedTimeLimit = localStorage.getItem('diagnostic_time_limit');
    
    if (!storedSessionId || !storedEmail) {
      navigate('/');
      return;
    }
    
    setSessionId(storedSessionId);
    setEmail(storedEmail);
    if (storedSection) setSection(storedSection);
    if (storedSectionName) setSectionName(storedSectionName);
    if (storedTimeLimit) setTimeRemaining(parseInt(storedTimeLimit));
    loadQuestions(storedSessionId);
  }, []);

  useEffect(() => {
    if (questions.length > 0 && !onBreak) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleModuleSubmit(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [questions, onBreak]);

  useEffect(() => {
    if (onBreak) {
      const breakTimer = setInterval(() => {
        setBreakTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(breakTimer);
            startMathSection();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(breakTimer);
    }
  }, [onBreak]);

  const loadQuestions = async (sessId) => {
    try {
      const storedQuestions = localStorage.getItem('diagnostic_questions');
      if (storedQuestions) {
        const parsed = JSON.parse(storedQuestions);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Deduplicate by question id
          const seen = new Set();
          const unique = parsed.filter(q => {
            if (!q || !q.id || seen.has(q.id)) return false;
            seen.add(q.id);
            return true;
          });
          setQuestions(unique);
          setLoading(false);
          return;
        }
      }
      
      // If localStorage is empty/invalid, try to fetch from API using session ID
      if (sessId) {
        try {
          const storedEmail = localStorage.getItem('diagnostic_email');
          const response = await axios.post(`${API}/api/diagnostic/start`, {
            email: storedEmail || 'guest@test.com'
          });
          
          if (response.data.questions && response.data.questions.length > 0) {
            // Deduplicate by question id
            const seen = new Set();
            const unique = response.data.questions.filter(q => {
              if (!q || !q.id || seen.has(q.id)) return false;
              seen.add(q.id);
              return true;
            });
            
            // Update localStorage and session
            localStorage.setItem('diagnostic_session_id', response.data.session_id);
            localStorage.setItem('diagnostic_questions', JSON.stringify(unique));
            localStorage.setItem('diagnostic_section', response.data.section || 'rw');
            localStorage.setItem('diagnostic_section_name', response.data.section_name || 'Reading and Writing');
            localStorage.setItem('diagnostic_time_limit', response.data.time_limit || '1920');
            
            setSessionId(response.data.session_id);
            setQuestions(unique);
            setTimeRemaining(response.data.time_limit || 1920);
            setLoading(false);
            return;
          }
        } catch (apiError) {
          console.error('Error fetching questions from API:', apiError);
        }
      }
      
      // If we still don't have questions, redirect to home
      console.error('No questions available in localStorage or from API');
      navigate('/');
    } catch (error) {
      console.error('Error loading questions:', error);
      navigate('/');
    }
  };

  const startMathSection = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/diagnostic/${sessionId}/start-math`);
      
      // Deduplicate questions by id (with null checks)
      const questions = response.data.questions || [];
      const seen = new Set();
      const uniqueQuestions = questions.filter(q => {
        if (!q || !q.id || seen.has(q.id)) return false;
        seen.add(q.id);
        return true;
      });
      
      setSection(response.data.section);
      setSectionName(response.data.section_name);
      setModuleNumber(response.data.module);
      setQuestions(uniqueQuestions);
      setTimeRemaining(response.data.time_limit);
      setCurrentQuestionIndex(0);
      setAnswers({});
      setBookmarkedQuestions(new Set()); // Reset bookmarks for new section
      setQuestionStartTime(Date.now());
      setOnBreak(false);
      setLoading(false);
      
      // Update localStorage with deduplicated questions
      localStorage.setItem('diagnostic_questions', JSON.stringify(uniqueQuestions));
      localStorage.setItem('diagnostic_section', response.data.section);
      localStorage.setItem('diagnostic_section_name', response.data.section_name);
      localStorage.setItem('diagnostic_time_limit', response.data.time_limit);
    } catch (error) {
      console.error('Error starting Math section:', error);
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswer = (answer) => {
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    setAnswers({
      ...answers,
      [questions[currentQuestionIndex].id]: {
        answer,
        time_spent: timeSpent
      }
    });
  };

  const goToQuestion = (index) => {
    setCurrentQuestionIndex(index);
    setQuestionStartTime(Date.now());
  };

  // Toggle bookmark for current question
  const toggleBookmark = (questionId) => {
    setBookmarkedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  // Jump to next flagged question
  const jumpToNextFlagged = () => {
    if (bookmarkedQuestions.size === 0) return;
    
    const flaggedIndices = questions
      .map((q, idx) => bookmarkedQuestions.has(q.id) ? idx : -1)
      .filter(idx => idx !== -1);
    
    if (flaggedIndices.length === 0) return;
    
    // Find next flagged question after current index
    const nextFlagged = flaggedIndices.find(idx => idx > currentQuestionIndex);
    
    // If no flagged question after current, wrap to first flagged
    const targetIndex = nextFlagged !== undefined ? nextFlagged : flaggedIndices[0];
    goToQuestion(targetIndex);
  };

  const handleModuleSubmit = async (autoSubmit = false) => {
    try {
      setLoading(true);
      
      // Prepare answers
      const answersList = questions.map(q => ({
        question_id: q.id,
        answer: answers[q.id]?.answer || '',
        time_spent: answers[q.id]?.time_spent || 0
      }));

      const response = await axios.post(
        `${API}/api/diagnostic/${sessionId}/submit-module`,
        {
          section: section,
          module_number: moduleNumber,
          answers: answersList
        }
      );

      if (response.data.completed) {
        // Full test completed
        const token = localStorage.getItem('token');
        
        // Store scores
        localStorage.setItem('diagnostic_final_score', response.data.final_score);
        localStorage.setItem('diagnostic_rw_score', response.data.rw_score);
        localStorage.setItem('diagnostic_math_score', response.data.math_score);
        
        // Store score ranges for prediction bands
        if (response.data.score_ranges) {
          localStorage.setItem('diagnostic_score_ranges', JSON.stringify(response.data.score_ranges));
        }
        
        if (token) {
          navigate(`/results/${sessionId}`);
        } else {
          navigate('/diagnostic-results');
        }
      } else if (response.data.show_break) {
        // R&W section complete, show break
        setRwScore(response.data.rw_score);
        setOnBreak(true);
        setBreakTimeRemaining(response.data.break_duration || 600);
        setShowSubmitDialog(false);
        setLoading(false);
      } else {
        // Moving to next module (within same section)
        const nextQuestions = response.data.questions || [];
        
        // Validate we have questions before proceeding
        if (nextQuestions.length === 0) {
          console.error('No questions received for next module');
          alert('Error loading next module. Please try again.');
          setLoading(false);
          return;
        }
        
        // Deduplicate questions by id - REPLACE all questions, don't merge
        const seen = new Set();
        const uniqueQuestions = nextQuestions.filter(q => {
          if (!q || !q.id || seen.has(q.id)) return false;
          seen.add(q.id);
          return true;
        });
        
        // Clear localStorage first to prevent any merge issues
        localStorage.removeItem('diagnostic_questions');
        
        // Update all state at once
        setSection(response.data.section);
        setSectionName(response.data.section_name);
        setModuleNumber(response.data.module);
        setQuestions(uniqueQuestions);
        setTimeRemaining(response.data.time_limit);
        setCurrentQuestionIndex(0);
        setAnswers({});
        setBookmarkedQuestions(new Set());
        setQuestionStartTime(Date.now());
        setShowSubmitDialog(false);
        setLoading(false);
        
        // Update localStorage with the new module's questions
        localStorage.setItem('diagnostic_questions', JSON.stringify(uniqueQuestions));
        localStorage.setItem('diagnostic_section', response.data.section);
        localStorage.setItem('diagnostic_section_name', response.data.section_name);
        localStorage.setItem('diagnostic_time_limit', response.data.time_limit);
      }
    } catch (error) {
      console.error('Error submitting module:', error);
      alert('Error submitting test. Please try again.');
      setLoading(false);
    }
  };

  const handleExit = () => {
    localStorage.removeItem('diagnostic_session_id');
    localStorage.removeItem('diagnostic_email');
    localStorage.removeItem('diagnostic_questions');
    localStorage.removeItem('diagnostic_section');
    localStorage.removeItem('diagnostic_section_name');
    localStorage.removeItem('diagnostic_time_limit');
    navigate('/');
  };

  // Get section color
  const getSectionColor = () => {
    return section === 'rw' ? '#6366f1' : '#0030b3';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <SEOHead {...seoConfigs.diagnosticTest} />
        <div>Loading diagnostic test...</div>
      </div>
    );
  }

  // Break page between R&W and Math sections
  if (onBreak) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <SEOHead {...seoConfigs.diagnosticTest} />
        <Header variant="minimal" />

        {/* Break Content */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '2rem' }}>
          <div style={{ 
            background: 'white', 
            borderRadius: '16px', 
            padding: '3rem', 
            maxWidth: '600px', 
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem', color: '#1a202c' }}>
              Reading and Writing Complete!
            </h1>
            
            <p style={{ fontSize: '1.1rem', color: '#666', marginBottom: '2rem' }}>
              Take a 10-minute break before starting the Math section
            </p>

            {/* Break Timer */}
            <div style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px',
              padding: '2rem',
              marginBottom: '2rem'
            }}>
              <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                Break Time Remaining
              </div>
              <div style={{ 
                fontSize: '3rem', 
                fontWeight: '700', 
                color: 'white',
                fontFamily: 'monospace'
              }}>
                {formatTime(breakTimeRemaining)}
              </div>
            </div>

            <Button 
              onClick={startMathSection}
              style={{ 
                width: '100%',
                padding: '1rem',
                fontSize: '1.1rem',
                fontWeight: '600'
              }}
            >
              Start Math Section Now
            </Button>

            <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '1rem' }}>
              Or wait for the timer to finish
            </p>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = answers[currentQuestion?.id];

  // Diagnostic test right content with section indicator, timer, and exit button
  const diagnosticRightContent = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
      {/* Section indicator */}
      <div style={{ 
        background: getSectionColor(), 
        padding: '0.5rem 1rem', 
        borderRadius: '6px',
        color: 'white',
        fontWeight: '600',
        fontSize: '0.9rem'
      }}>
        {sectionName}
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.25rem', fontWeight: '600', color: 'white' }}>
        <Clock size={24} />
        <span>{formatTime(timeRemaining)}</span>
      </div>
      <Button 
        onClick={() => setShowExitDialog(true)}
        variant="outline"
        style={{ 
          background: 'white',
          color: '#0030b3',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}
      >
        <X size={20} />
        Exit Test
      </Button>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SEOHead {...seoConfigs.diagnosticTest} />
      <Header 
        variant="minimal" 
        onLogoClick={() => setShowExitDialog(true)}
        rightContent={diagnosticRightContent}
      />

      {/* Main Content */}
      <div style={{ flex: 1, background: '#f5f5f5' }}>
        <div style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 2rem' }}>
          {/* Section and Module Title */}
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: getSectionColor(), marginBottom: '0.25rem', fontFamily: 'Arial, sans-serif' }}>
              Diagnostic Test - {sectionName}
            </h1>
            <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
              Module {moduleNumber} • Question {currentQuestionIndex + 1} of {questions.length}
            </p>
          </div>

          <div style={{ background: 'white', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            {/* Question Navigation */}
            <div style={{ marginBottom: '2rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
              {questions.map((q, idx) => {
                const isBookmarked = bookmarkedQuestions.has(q.id);
                const isAnswered = answers[q.id];
                const isCurrent = idx === currentQuestionIndex;
                
                return (
                  <button
                    key={q.id}
                    onClick={() => goToQuestion(idx)}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '4px',
                      border: `2px solid ${isCurrent ? getSectionColor() : isBookmarked ? '#f59e0b' : '#ddd'}`,
                      background: isAnswered ? getSectionColor() : isBookmarked ? '#fef3c7' : 'white',
                      color: isAnswered ? 'white' : (isCurrent ? getSectionColor() : '#666'),
                      fontWeight: '600',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                    title={isBookmarked ? 'Bookmarked for review' : ''}
                  >
                    {idx + 1}
                    {isBookmarked && (
                      <Flag 
                        size={10} 
                        style={{ 
                          position: 'absolute', 
                          top: '2px', 
                          right: '2px',
                          color: isAnswered ? 'white' : '#f59e0b'
                        }} 
                      />
                    )}
                  </button>
                );
              })}
              
              {/* Bookmark count indicator */}
              {bookmarkedQuestions.size > 0 && (
                <div style={{ 
                  marginLeft: '1rem', 
                  padding: '0.25rem 0.75rem', 
                  background: '#fef3c7', 
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  color: '#92400e',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  <Flag size={12} />
                  {bookmarkedQuestions.size} flagged
                </div>
              )}
              
              {/* Jump to Flagged button */}
              {bookmarkedQuestions.size > 0 && (
                <button
                  onClick={jumpToNextFlagged}
                  style={{
                    padding: '0.25rem 0.75rem',
                    background: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#d97706'}
                  onMouseOut={(e) => e.target.style.background = '#f59e0b'}
                  data-testid="jump-to-flagged-btn"
                >
                  <ChevronRight size={14} />
                  Jump to Flagged
                </button>
              )}
            </div>

            {/* Question */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>
                  Question {currentQuestionIndex + 1}
                </h3>
                <button
                  onClick={() => toggleBookmark(currentQuestion?.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    border: `2px solid ${bookmarkedQuestions.has(currentQuestion?.id) ? '#f59e0b' : '#ddd'}`,
                    borderRadius: '6px',
                    background: bookmarkedQuestions.has(currentQuestion?.id) ? '#fef3c7' : 'white',
                    color: bookmarkedQuestions.has(currentQuestion?.id) ? '#92400e' : '#666',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  data-testid="bookmark-btn"
                >
                  <Flag size={16} fill={bookmarkedQuestions.has(currentQuestion?.id) ? '#f59e0b' : 'none'} />
                  {bookmarkedQuestions.has(currentQuestion?.id) ? 'Flagged' : 'Flag for Review'}
                </button>
              </div>
              <div style={{ fontSize: '1rem', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                <MathText>{currentQuestion?.question_text}</MathText>
              </div>

              {currentQuestion?.image_url && (
                <img 
                  src={currentQuestion.image_url} 
                  alt="Question diagram" 
                  style={{ 
                    maxWidth: 'min(400px, 100%)', 
                    marginBottom: '1.5rem', 
                    borderRadius: '4px',
                    display: 'block'
                  }}
                />
              )}

              {/* Answer Input */}
              {currentQuestion?.question_type === 'student_produced_response' ? (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Enter your answer:
                  </label>
                  <input
                    type="text"
                    value={currentAnswer?.answer || ''}
                    onChange={(e) => handleAnswer(e.target.value)}
                    placeholder="Type your numerical answer"
                    style={{
                      width: '100%',
                      maxWidth: '300px',
                      padding: '0.75rem',
                      fontSize: '1rem',
                      border: '2px solid #ddd',
                      borderRadius: '4px',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = getSectionColor()}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {currentQuestion?.choices?.map((choice) => (
                    <button
                      key={choice}
                      onClick={() => handleAnswer(choice)}
                      style={{
                        padding: '1rem',
                        border: `2px solid ${currentAnswer?.answer === choice ? getSectionColor() : '#ddd'}`,
                        background: currentAnswer?.answer === choice ? (section === 'rw' ? '#eef2ff' : '#e6f0ff') : 'white',
                        borderRadius: '4px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      <strong>{choice}.</strong> <MathText>{currentQuestion.choice_texts?.[choice]}</MathText>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Navigation */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
              <Button
                onClick={() => goToQuestion(Math.max(0, currentQuestionIndex - 1))}
                disabled={currentQuestionIndex === 0}
                variant="outline"
              >
                <ChevronLeft size={20} />
                Previous
              </Button>
              
              {currentQuestionIndex === questions.length - 1 ? (
                <Button 
                  onClick={() => setShowSubmitDialog(true)}
                  style={{ background: getSectionColor() }}
                >
                  Submit {sectionName} Module {moduleNumber}
                </Button>
              ) : (
                <Button 
                  onClick={() => goToQuestion(currentQuestionIndex + 1)}
                  style={{ background: getSectionColor() }}
                >
                  Next
                  <ChevronRight size={20} />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer variant="minimal" />

      {/* Submit Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit {sectionName} Module {moduleNumber}?</DialogTitle>
            <DialogDescription>
              You have answered {Object.keys(answers).length} out of {questions.length} questions.
              {Object.keys(answers).length < questions.length && (
                <span style={{ display: 'block', marginTop: '0.5rem', color: '#d32f2f' }}>
                  Warning: You have unanswered questions.
                </span>
              )}
              {bookmarkedQuestions.size > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', color: '#92400e' }}>
                  <Flag size={14} />
                  You have {bookmarkedQuestions.size} question{bookmarkedQuestions.size > 1 ? 's' : ''} flagged for review.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)} style={{ flex: 1 }}>
              Review
            </Button>
            <Button onClick={() => { setShowSubmitDialog(false); handleModuleSubmit(); }} style={{ flex: 1 }}>
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exit Dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Exit Test?</DialogTitle>
            <DialogDescription>
              Your progress will not be saved.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <Button variant="outline" onClick={() => setShowExitDialog(false)} style={{ flex: 1 }}>
              Cancel
            </Button>
            <Button onClick={handleExit} style={{ flex: 1, background: '#d32f2f' }}>
              Exit Test
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Calculator - Only show for Math section */}
      {section === 'math' && (
        <button
          onClick={() => setShowCalculator(true)}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999
          }}
          title="Open Calculator"
        >
          <Calculator size={28} color="white" />
        </button>
      )}

      {/* Calculator Modal */}
      <Dialog open={showCalculator} onOpenChange={setShowCalculator}>
        <DialogContent style={{ 
          maxWidth: 'min(900px, 95vw)', 
          width: '95vw',
          height: 'min(700px, 85vh)', 
          maxHeight: '85vh',
          padding: 0,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <DialogHeader style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
            <DialogTitle>Desmos Calculator</DialogTitle>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button
                onClick={() => setCalculatorType('scientific')}
                style={{
                  padding: '0.5rem 1.5rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: calculatorType === 'scientific' ? '#667eea' : '#e5e7eb',
                  color: calculatorType === 'scientific' ? 'white' : '#666',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Scientific
              </button>
              <button
                onClick={() => setCalculatorType('graphing')}
                style={{
                  padding: '0.5rem 1.5rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: calculatorType === 'graphing' ? '#667eea' : '#e5e7eb',
                  color: calculatorType === 'graphing' ? 'white' : '#666',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Graphing
              </button>
            </div>
          </DialogHeader>
          <div style={{ width: '100%', flex: 1, minHeight: 0 }}>
            <iframe
              src={calculatorType === 'scientific' 
                ? 'https://www.desmos.com/scientific' 
                : 'https://www.desmos.com/calculator'}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Desmos Calculator"
              key={calculatorType}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
